
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useUpdateCoupon } from "@/hooks/useCoupons";

interface EditCouponDialogProps {
  coupon: {
    id: string;
    code: string;
    name: string;
    description?: string;
    discount_type: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits';
    discount_value: number;
    credits_per_period?: number;
    max_discount_amount?: number;
    min_order_amount?: number;
    usage_limit?: number;
    valid_from: string;
    valid_until?: string;
    coupon_services?: Array<{
      services: { id: string; name: string; };
    }>;
  };
  services: Array<{ id: string; name: string; }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCouponUpdated?: () => void;
}

interface EditCouponFormData {
  name: string;
  description?: string;
  discount_type: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits';
  discount_value?: number;
  credits_per_period?: number;
  max_discount_amount?: number;
  min_order_amount?: number;
  usage_limit?: number;
  valid_from?: string;
  valid_until?: string;
  service_ids: string[];
}

const EditCouponDialog = ({ coupon, services, open, onOpenChange, onCouponUpdated }: EditCouponDialogProps) => {
  const updateCouponMutation = useUpdateCoupon();

  const form = useForm<EditCouponFormData>({
    defaultValues: {
      name: coupon.name,
      description: coupon.description || '',
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      credits_per_period: coupon.credits_per_period || undefined,
      max_discount_amount: coupon.max_discount_amount || undefined,
      min_order_amount: coupon.min_order_amount || undefined,
      usage_limit: coupon.usage_limit || undefined,
      valid_from: coupon.valid_from ? new Date(coupon.valid_from).toISOString().split('T')[0] : '',
      valid_until: coupon.valid_until ? new Date(coupon.valid_until).toISOString().split('T')[0] : '',
      service_ids: coupon.coupon_services?.map(cs => cs.services.id) || [],
    },
  });

  const discountType = form.watch('discount_type');
  const isCreditBased = discountType === 'weekly_credits' || discountType === 'monthly_credits';

  // Reset form when coupon changes
  useEffect(() => {
    form.reset({
      name: coupon.name,
      description: coupon.description || '',
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      credits_per_period: coupon.credits_per_period || undefined,
      max_discount_amount: coupon.max_discount_amount || undefined,
      min_order_amount: coupon.min_order_amount || undefined,
      usage_limit: coupon.usage_limit || undefined,
      valid_from: coupon.valid_from ? new Date(coupon.valid_from).toISOString().split('T')[0] : '',
      valid_until: coupon.valid_until ? new Date(coupon.valid_until).toISOString().split('T')[0] : '',
      service_ids: coupon.coupon_services?.map(cs => cs.services.id) || [],
    });
  }, [coupon, form]);

  const onSubmit = async (data: EditCouponFormData) => {
    try {
      const updates = {
        name: data.name,
        description: data.description,
        discount_type: data.discount_type,
        discount_value: isCreditBased ? 0 : (data.discount_value || 0),
        credits_per_period: isCreditBased ? data.credits_per_period : null,
        max_discount_amount: !isCreditBased ? (data.max_discount_amount || null) : null,
        min_order_amount: !isCreditBased ? (data.min_order_amount || null) : null,
        usage_limit: data.usage_limit || null,
        valid_from: data.valid_from ? new Date(data.valid_from).toISOString() : coupon.valid_from,
        valid_until: data.valid_until ? new Date(data.valid_until).toISOString() : null,
      };

      await updateCouponMutation.mutateAsync({
        couponId: coupon.id,
        updates,
        serviceIds: data.service_ids
      });

      onOpenChange(false);
      onCouponUpdated?.();
    } catch (error) {
      console.error('Error updating coupon:', error);
    }
  };

  const handleServiceToggle = (serviceId: string, checked: boolean) => {
    const currentServices = form.getValues('service_ids');
    if (checked) {
      form.setValue('service_ids', [...currentServices, serviceId]);
    } else {
      form.setValue('service_ids', currentServices.filter(id => id !== serviceId));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Coupon</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-coupon-name">Coupon Name *</Label>
              <Input
                id="edit-coupon-name"
                {...form.register('name', { required: true })}
                placeholder="Enter coupon name"
              />
            </div>

            <div>
              <Label>Coupon Code</Label>
              <Input
                value={coupon.code}
                disabled
                className="bg-gray-100"
                placeholder="Code cannot be changed"
              />
              <p className="text-xs text-gray-500 mt-1">Coupon code cannot be modified</p>
            </div>
          </div>

          <div>
            <Label htmlFor="edit-coupon-description">Description</Label>
            <Textarea
              id="edit-coupon-description"
              {...form.register('description')}
              placeholder="Enter coupon description (optional)"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Discount Type *</Label>
              <Select
                value={form.watch('discount_type')}
                onValueChange={(value: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits') => 
                  form.setValue('discount_type', value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed Amount (£)</SelectItem>
                  <SelectItem value="weekly_credits">Weekly Credits</SelectItem>
                  <SelectItem value="monthly_credits">Monthly Credits</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!isCreditBased && (
              <div>
                <Label htmlFor="edit-discount-value">
                  Discount Value * {form.watch('discount_type') === 'percentage' ? '(%)' : '(£)'}
                </Label>
                <Input
                  id="edit-discount-value"
                  type="number"
                  step={form.watch('discount_type') === 'percentage' ? '1' : '0.01'}
                  min="0"
                  max={form.watch('discount_type') === 'percentage' ? '100' : undefined}
                  {...form.register('discount_value', { 
                    required: !isCreditBased, 
                    min: 0,
                    max: form.watch('discount_type') === 'percentage' ? 100 : undefined
                  })}
                  placeholder={form.watch('discount_type') === 'percentage' ? 'e.g., 20' : 'e.g., 10.00'}
                />
              </div>
            )}

            {isCreditBased && (
              <div>
                <Label htmlFor="edit-credits-per-period">
                  Credits Per {discountType === 'weekly_credits' ? 'Week' : 'Month'} *
                </Label>
                <Input
                  id="edit-credits-per-period"
                  type="number"
                  min="1"
                  {...form.register('credits_per_period', { 
                    required: isCreditBased, 
                    min: 1
                  })}
                  placeholder="e.g., 3"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Number of free sessions per {discountType === 'weekly_credits' ? 'week' : 'month'}
                </p>
              </div>
            )}
          </div>

          {!isCreditBased && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-max-discount">Maximum Discount Amount (£)</Label>
                <Input
                  id="edit-max-discount"
                  type="number"
                  step="0.01"
                  min="0"
                  {...form.register('max_discount_amount')}
                  placeholder="Optional - e.g., 50.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  For percentage discounts, cap the maximum discount amount
                </p>
              </div>

              <div>
                <Label htmlFor="edit-min-order">Minimum Order Amount (£)</Label>
                <Input
                  id="edit-min-order"
                  type="number"
                  step="0.01"
                  min="0"
                  {...form.register('min_order_amount')}
                  placeholder="Optional - e.g., 25.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum order value required to use this coupon
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-usage-limit">Usage Limit</Label>
              <Input
                id="edit-usage-limit"
                type="number"
                min="1"
                {...form.register('usage_limit')}
                placeholder="Optional - e.g., 100"
              />
              <p className="text-xs text-gray-500 mt-1">
                {isCreditBased 
                  ? 'Maximum number of users who can claim this coupon'
                  : 'Maximum number of times this coupon can be used'
                }
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-valid-from">Valid From</Label>
              <Input
                id="edit-valid-from"
                type="date"
                {...form.register('valid_from')}
              />
            </div>

            <div>
              <Label htmlFor="edit-valid-until">Valid Until</Label>
              <Input
                id="edit-valid-until"
                type="date"
                {...form.register('valid_until')}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty for no expiry date
              </p>
            </div>
          </div>

          <div>
            <Label>Applicable Services *</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 p-4 border rounded-lg max-h-40 overflow-y-auto">
              {services.map((service) => (
                <div key={service.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`edit-service-${service.id}`}
                    checked={form.watch('service_ids').includes(service.id)}
                    onCheckedChange={(checked) => handleServiceToggle(service.id, checked as boolean)}
                  />
                  <Label htmlFor={`edit-service-${service.id}`} className="text-sm">
                    {service.name}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Select which services this coupon applies to
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateCouponMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateCouponMutation.isPending || form.watch('service_ids').length === 0}
            >
              {updateCouponMutation.isPending ? 'Updating...' : 'Update Coupon'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCouponDialog;
